/**
 * Catálogo único de planos e limites do UpClinic.
 * Fonte da verdade para landing, checkout e enforcement no app.
 *
 * Alinhado à tabela pública + site clinicupapp.com/planos:
 * - Starter: 10 pacientes, 1 profissional, telemedicina 50/mês, IA
 * - Solo: pacientes ilimitados, 1 profissional, cobrança recorrente (site 2026)
 * - Professional / Clínica: pacientes ilimitados, 5 profissionais, IA, tele avançada
 * - Plus: pacientes ilimitados, até 15 profissionais (site 2026)
 * - Enterprise: tudo ilimitado + Home Care GPS + TISS/SNGPC + suporte 24/7
 * IA médica disponível em todos os planos (incluindo Trial e Starter).
 */
import { STRIPE_PRICE_IDS } from './pricing';

export type PlanTier = 'trial' | 'starter' | 'solo' | 'professional' | 'plus' | 'enterprise';

export interface PlanFeatureFlags {
  hasAI: boolean;
  hasAdvancedReports: boolean;
  hasPharmacy: boolean;
  hasLaboratory: boolean;
  hasFinancialModule: boolean;
  hasInventory: boolean;
  hasHomeCare: boolean;
  hasTiss: boolean;
  hasSngpc: boolean;
  hasInjectables: boolean;
  hasApiAccess: boolean;
  hasRecurringBilling: boolean;
}

export interface PlanLimitsDefinition extends PlanFeatureFlags {
  maxPatients: number; // Infinity = ilimitado
  maxProfessionals: number;
  maxTelemedicinePerMonth: number;
  maxStorageGB: number;
  maxAiAnalysesPerMonth: number;
  supportLevel: 'email' | 'priority' | '24/7' | '24/7+gestor';
  planName: string;
  planId: PlanTier;
  isUnlimited: boolean;
}

/** Price IDs Live atuais + IDs legados (assinaturas antigas ainda ativas). */
export const PRICE_ID_TO_PLAN: Record<string, PlanTier> = {
  // —— Landing / Stripe antigos (Starter / Professional / Enterprise) ——
  // Nota: Solo fundador (buy.stripe.com/eVq7sM…00) reutiliza STARTER.MONTHLY.
  // Não remapeamos para 'solo' aqui — decisão do produto (ver PR).
  [STRIPE_PRICE_IDS.STARTER.MONTHLY]: 'starter',
  [STRIPE_PRICE_IDS.STARTER.ANNUAL]: 'starter',
  [STRIPE_PRICE_IDS.PROFESSIONAL.MONTHLY]: 'professional',
  [STRIPE_PRICE_IDS.PROFESSIONAL.ANNUAL]: 'professional',
  [STRIPE_PRICE_IDS.ENTERPRISE.MONTHLY]: 'enterprise',
  [STRIPE_PRICE_IDS.ENTERPRISE.ANNUAL]: 'enterprise',

  // —— Site 2026 (clinicupapp.com/planos) ——
  'price_1UN8MPJgDiwrjPst0smtTBZl': 'solo', // Solo mensal R$ 59
  'price_1UN8MPJgDiwrjPstKCthdZgP': 'solo', // Solo anual R$ 590
  'price_1UN8MQJgDiwrjPstxZ9yxnq3': 'professional', // Clínica mensal R$ 129 (até 5)
  'price_1UN8MRJgDiwrjPst7hGPyJQs': 'professional', // Clínica anual R$ 1.290
  'price_1UN8MSJgDiwrjPstCTZpyZS3': 'plus', // Plus mensal R$ 249 (até 15)
  'price_1UN8MSJgDiwrjPstcISmdac7': 'plus', // Plus anual R$ 2.490

  // —— Legados Standard → Starter ——
  'price_1RjgHrJgDiwrjPstqVyFU4Io': 'starter',
  'price_1RjgJFJgDiwrjPstbKo0h4NO': 'starter',

  // —— Legados Enterprise ——
  'price_1RjgIgJgDiwrjPsteazgeGb6': 'enterprise',
  'price_1RjgKBJgDiwrjPst0DwndBjl': 'enterprise',
};

/** metadata.plan dos Prices novos do site → tier do app */
const METADATA_PLAN_TO_TIER: Record<string, PlanTier> = {
  solo: 'solo',
  clinica: 'professional',
  clinical: 'professional',
  professional: 'professional',
  plus: 'plus',
  starter: 'starter',
  enterprise: 'enterprise',
};

export interface ResolvePlanOptions {
  /** Assinatura Stripe active/trialing — não cair em starter para Price ID desconhecido */
  hasActivePaidSubscription?: boolean;
  /** price.metadata.plan (ex.: solo / clinica / plus) como segunda fonte */
  priceMetadataPlan?: string | null;
}

export const PLAN_LIMITS_CATALOG: Record<PlanTier, PlanLimitsDefinition> = {
  trial: {
    maxPatients: Infinity,
    maxProfessionals: Infinity,
    maxTelemedicinePerMonth: Infinity,
    maxStorageGB: Infinity,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: true,
    hasPharmacy: true,
    hasLaboratory: true,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: true,
    hasTiss: true,
    hasSngpc: true,
    hasInjectables: true,
    hasApiAccess: true,
    hasRecurringBilling: true,
    supportLevel: 'email',
    planName: 'Trial Gratuito (7 dias)',
    planId: 'trial',
    isUnlimited: true,
  },
  starter: {
    maxPatients: 10,
    maxProfessionals: 1,
    maxTelemedicinePerMonth: 50,
    maxStorageGB: 5,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: false,
    hasPharmacy: false,
    hasLaboratory: false,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: false,
    hasTiss: false,
    hasSngpc: false,
    hasInjectables: false,
    hasApiAccess: false,
    hasRecurringBilling: false,
    supportLevel: 'email',
    planName: 'Starter',
    planId: 'starter',
    isUnlimited: false,
  },
  /** Solo (site 2026): profissional solo — ilimitado em pacientes, 1 profissional, cobrança recorrente */
  solo: {
    maxPatients: Infinity,
    maxProfessionals: 1,
    maxTelemedicinePerMonth: Infinity,
    maxStorageGB: 25,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: true,
    hasPharmacy: true,
    hasLaboratory: true,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: false,
    hasTiss: false,
    hasSngpc: false,
    hasInjectables: true,
    hasApiAccess: true,
    hasRecurringBilling: true,
    supportLevel: 'priority',
    planName: 'Solo',
    planId: 'solo',
    isUnlimited: false,
  },
  professional: {
    maxPatients: Infinity,
    maxProfessionals: 5,
    maxTelemedicinePerMonth: Infinity,
    maxStorageGB: 25,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: true,
    hasPharmacy: true,
    hasLaboratory: true,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: false,
    hasTiss: false,
    hasSngpc: false,
    hasInjectables: true,
    hasApiAccess: true,
    hasRecurringBilling: true,
    supportLevel: 'priority',
    planName: 'Professional',
    planId: 'professional',
    isUnlimited: false,
  },
  /** Plus (site 2026): até 15 profissionais; features alinhadas ao Enterprise sem ilimitado total */
  plus: {
    maxPatients: Infinity,
    maxProfessionals: 15,
    maxTelemedicinePerMonth: Infinity,
    maxStorageGB: Infinity,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: true,
    hasPharmacy: true,
    hasLaboratory: true,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: true,
    hasTiss: true,
    hasSngpc: true,
    hasInjectables: true,
    hasApiAccess: true,
    hasRecurringBilling: true,
    supportLevel: '24/7',
    planName: 'Plus',
    planId: 'plus',
    isUnlimited: false,
  },
  enterprise: {
    maxPatients: Infinity,
    maxProfessionals: Infinity,
    maxTelemedicinePerMonth: Infinity,
    maxStorageGB: Infinity,
    maxAiAnalysesPerMonth: Infinity,
    hasAI: true,
    hasAdvancedReports: true,
    hasPharmacy: true,
    hasLaboratory: true,
    hasFinancialModule: true,
    hasInventory: true,
    hasHomeCare: true,
    hasTiss: true,
    hasSngpc: true,
    hasInjectables: true,
    hasApiAccess: true,
    hasRecurringBilling: true,
    supportLevel: '24/7+gestor',
    planName: 'Enterprise',
    planId: 'enterprise',
    isUnlimited: true,
  },
};

/** Features usadas pelo SubscriptionGuard / isFeatureAvailable */
export type PlanFeatureKey =
  | 'patients'
  | 'professionals'
  | 'telemedicineSessions'
  | 'storageGB'
  | 'aiAnalyses'
  | 'pharmacyIntegration'
  | 'laboratoryIntegration'
  | 'apiAccess'
  | 'hasAI'
  | 'hasHomeCare'
  | 'hasTiss'
  | 'hasSngpc'
  | 'hasInjectables'
  | 'hasRecurringBilling'
  | 'hasAdvancedReports'
  | 'hasFinancialModule'
  | 'managerDedicated';

function resolvePlanFromMetadata(metadataPlan: string | null | undefined): PlanTier | null {
  if (!metadataPlan) return null;
  const key = metadataPlan.trim().toLowerCase();
  return METADATA_PLAN_TO_TIER[key] ?? null;
}

/**
 * Resolve o tier a partir do Stripe Price ID.
 * Com assinatura active/trialing e ID desconhecido: tenta metadata.plan;
 * se ainda falhar, loga erro e concede 'professional' (não travar quem pagou).
 */
export function resolvePlanFromPriceId(
  priceId: string | null | undefined,
  options: ResolvePlanOptions = {}
): PlanTier {
  const { hasActivePaidSubscription = false, priceMetadataPlan } = options;

  if (priceId) {
    const mapped = PRICE_ID_TO_PLAN[priceId];
    if (mapped) return mapped;
  }

  const fromMeta = resolvePlanFromMetadata(priceMetadataPlan);
  if (fromMeta) {
    if (priceId && !PRICE_ID_TO_PLAN[priceId]) {
      console.warn(
        '⚠️ [PLAN-LIMITS] Price ID não mapeado; usando price.metadata.plan:',
        priceId,
        priceMetadataPlan
      );
    }
    return fromMeta;
  }

  if (hasActivePaidSubscription) {
    console.error(
      '❌ [PLAN-LIMITS] Price ID desconhecido com assinatura active/trialing — concedendo professional:',
      priceId || '(ausente)'
    );
    return 'professional';
  }

  if (!priceId) return 'starter';
  return 'starter';
}

export function getPlanLimits(tier: PlanTier): PlanLimitsDefinition {
  return PLAN_LIMITS_CATALOG[tier] || PLAN_LIMITS_CATALOG.starter;
}

export function isNumericUnlimited(n: number): boolean {
  return !Number.isFinite(n);
}

export function featureAvailable(tier: PlanTier, feature: PlanFeatureKey): boolean {
  const limits = getPlanLimits(tier);
  switch (feature) {
    case 'pharmacyIntegration':
      return limits.hasPharmacy;
    case 'laboratoryIntegration':
      return limits.hasLaboratory;
    case 'apiAccess':
      return limits.hasApiAccess;
    case 'hasAI':
    case 'aiAnalyses':
      // Análise clínica / IA liberada em todos os planos
      return true;
    case 'hasHomeCare':
      return limits.hasHomeCare;
    case 'hasTiss':
      return limits.hasTiss;
    case 'hasSngpc':
      return limits.hasSngpc;
    case 'hasInjectables':
      return limits.hasInjectables;
    case 'hasRecurringBilling':
      return limits.hasRecurringBilling;
    case 'hasAdvancedReports':
      return limits.hasAdvancedReports;
    case 'hasFinancialModule':
      return limits.hasFinancialModule;
    case 'managerDedicated':
      return limits.supportLevel === '24/7+gestor' || limits.supportLevel === '24/7';
    case 'patients':
      return isNumericUnlimited(limits.maxPatients) || limits.maxPatients > 0;
    case 'professionals':
      return isNumericUnlimited(limits.maxProfessionals) || limits.maxProfessionals > 0;
    case 'telemedicineSessions':
      return isNumericUnlimited(limits.maxTelemedicinePerMonth) || limits.maxTelemedicinePerMonth > 0;
    case 'storageGB':
      return isNumericUnlimited(limits.maxStorageGB) || limits.maxStorageGB > 0;
    default:
      return false;
  }
}
